import type { RouteLocation, RouteRecordNormalized } from '#vue-router'
import { type ComponentOptions, unref } from 'vue'
import { appPrefetch, layoutPrefetch } from '#layout-prefetch'
import { capturePrefetchState, isRedirectPredicted, useCoreLogger, useNuxtApp, useQueryClient, useSessionExpiry, useUserInfo, useUsername } from '#imports'

export interface PrefetchRouteOptions {
    /** Whether a gateway call made now would use the access token without refreshing it. */
    accessTokenFresh?: boolean
    /** In the browser: the route being left, whose layout stays mounted if `to` has the same one. */
    from?: RouteLocation
    isServer?: boolean
}

type Declaration = (() => void) | undefined

function layoutOf(to: RouteLocation): string | false {
    const layout = unref(to.meta.layout)

    return (typeof layout === 'object' ? unref(layout.name) : layout) ?? 'default'
}

/** The page's `prefetch` option, loading its chunk ahead of the router, which loads it next anyway. */
async function pageDeclaration(record: RouteRecordNormalized, to: RouteLocation): Promise<Declaration> {
    const component = record.components?.default
    // Until the router resolves a lazy page, its component is the loader.
    const page = typeof component === 'function' ? await (component as () => Promise<unknown>)() : component
    const prefetch = (page as ComponentOptions | undefined)?.prefetch

    return prefetch && (() => prefetch(to))
}

function appDeclaration(to: RouteLocation): Declaration {
    const prefetch = appPrefetch

    return prefetch && (() => prefetch(to))
}

/**
 * Starts the critical queries `to` declares, its pages' and `app.vue`'s `defineOptions({ prefetch })`
 * and its layout's `prefetch` export, before middleware and components ask for them.
 */
export function prefetchRoute(to: RouteLocation, { accessTokenFresh = false, from, isServer = import.meta.server }: PrefetchRouteOptions = {}) {
    // Unmatched means an error page, which has no layout and declares nothing.
    if (!to.matched.length || isRedirectPredicted(to, isServer)) return

    const hasSession = !!useSessionExpiry().value
    const { prefetchQueries } = useQueryClient()
    const logger = useCoreLogger().withTag('prefetch')
    // Left once the next navigation clears it; its declarations are for a page that won't render.
    const isCurrent = capturePrefetchState(useNuxtApp())
    const layout = layoutOf(to)
    const layoutMounts = !from || layoutOf(from) !== layout
    const unloaded = (error: unknown) => {
        logger.warn('a chunk failed to load', error)
        return undefined
    }
    const declarations: Promise<Declaration>[] = to.matched.map(record => pageDeclaration(record, to).catch(unloaded))
    declarations.push(Promise.resolve(appDeclaration(to)))
    const declare = (declaration: Declaration) => {
        if (declaration && isCurrent()) prefetchQueries(declaration)
    }

    if (layout && layoutPrefetch[layout]) {
        declarations.push(layoutPrefetch[layout]().then(prefetch => prefetch && (() => prefetch(to, { layoutMounts })), unloaded))
    }

    const declareAll = () => {
        for (const declaration of declarations) void declaration.then(declare)
    }

    if (!hasSession) return declareAll()

    // The header renders the username during SSR, so the server starts it beside user/info. The
    // browser keeps the one it hydrated with. A `username` cookie skips it: the cookie's player is
    // checked once user/info answers, and only a stranger's cookie asks the nameservice then.
    const declareUsername = () => {
        useUsername()
    }

    // A fresh token needs no refresh, so nothing waits on user/info; the declarations read the
    // token instead. The server still starts user/info first.
    if (accessTokenFresh) {
        if (isServer) {
            prefetchQueries(() => {
                useUserInfo({ server: true, lazy: false })
                declareUsername()
            })
        }
        return declareAll()
    }

    // Otherwise user/info goes first, so a stale token is refreshed once rather than by every call
    // beside it, the username's included. `00.user-status` defers to the browser's refresh.
    const userInfo = isServer
        ? prefetchQueries(() => useUserInfo({ server: true, lazy: false }))[0]
        : useUserInfo({ server: true, lazy: false }).refresh({ cause: 'refresh:manual', dedupe: 'defer' })

    if (isServer) declarations.push(Promise.resolve(declareUsername))

    void Promise.all([userInfo?.then(() => {}, () => {}), ...declarations])
        .then(([, ...loaded]) => loaded.forEach(declare))
}
